// hc-cart-upsells.js
// <harvest-cart-upsells> — fetches Shopify Product Recommendations for the
// highest-priced non-LTL item in the cart, filters out LTL + items already in
// the cart, renders click-to-add cards. Re-renders on cart:change. Falls back
// from "complementary" intent to "related" if too few complements are configured.
//
// Two visual variants based on the `variant` attribute:
//   - "drawer": vertical stack of 3 horizontal cards (image left, info right)
//   - "page":   grid of up to 6 product-card-style tiles (image top, info below)

(function () {
  'use strict';

  if (!window.customElements) return;

  var FETCH_LIMIT = 12;

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function formatMoney(cents) {
    var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    var locale = (window.Shopify && Shopify.locale) || 'en-US';
    return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(cents / 100);
  }

  function imgUrlForProduct(product) {
    var img = product.featured_image || (product.images && product.images[0]) || '';
    if (!img) return '';
    var src = typeof img === 'string' ? img : (img.src || '');
    if (!src) return '';
    return src.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i, '_240x.$1$2');
  }

  class HarvestCartUpsells extends HTMLElement {
    constructor() {
      super();
      this._onCartChanged = this._onCartChanged.bind(this);
      this._loading = false;
      this._skelTimer = null;
    }

    connectedCallback() {
      this._variant = this.getAttribute('variant') || 'drawer';
      this._showLimit = this._variant === 'pdp' ? 4 : 3;
      this._fixedContextId = this.getAttribute('data-context-product-id') || '';
      this._fixedContextId = this._fixedContextId ? parseInt(this._fixedContextId, 10) : 0;
      this._scheduleSkeleton();
      document.addEventListener('cart:change', this._onCartChanged);
      this._loadFromCartApi();
    }

    disconnectedCallback() {
      this._clearSkeletonTimer();
      document.removeEventListener('cart:change', this._onCartChanged);
    }

    _scheduleSkeleton() {
      var self = this;
      this._clearSkeletonTimer();
      this._skelTimer = window.setTimeout(function () {
        if (self.hasAttribute('data-has-products')) return;
        self._renderSkeleton();
      }, 150);
    }

    _clearSkeletonTimer() {
      if (this._skelTimer) {
        window.clearTimeout(this._skelTimer);
        this._skelTimer = null;
      }
    }

    _renderSkeleton() {
      // Skeleton uses the shared hc-skel primitives loaded by hc-skeleton.css.
      // Layout mirrors the upcoming hc-upsell-card markup so there is no shift
      // when real content replaces it. See docs/SKELETONS.md.
      // data-state="skeleton" lets hc-cart-upsells.css keep the host visible
      // (it normally hides on :not([data-has-products])).
      var listClass = (this._variant === 'page' || this._variant === 'pdp')
        ? 'hc-upsells__grid'
        : 'hc-upsells__stack';
      var heading = this.getAttribute('heading') || 'Pairs well with what is in your cart';
      var rows = '';
      for (var i = 0; i < this._showLimit; i++) {
        rows += '<div class="hc-skel-cart-line" aria-hidden="true">' +
                  '<div class="hc-skel hc-skel--media-1x1"></div>' +
                  '<div class="hc-skel-cart-line__text">' +
                    '<div class="hc-skel hc-skel--text-lg"></div>' +
                    '<div class="hc-skel hc-skel--text-sm"></div>' +
                  '</div>' +
                  '<div class="hc-skel hc-skel--pill"></div>' +
                '</div>';
      }
      this.setAttribute('data-state', 'skeleton');
      this.setAttribute('aria-busy', 'true');
      this.innerHTML =
        '<div class="hc-upsells__inner" data-skeleton="true">' +
          '<p class="hc-upsells__title">' + escapeHtml(heading) + '</p>' +
          '<div class="' + listClass + '" role="list">' + rows + '</div>' +
        '</div>';
    }

    _onCartChanged(event) {
      // On cart change, the recommendation set may change. Show skeleton again
      // (after 150ms delay) while the new set is fetched.
      this.removeAttribute('data-has-products');
      this._scheduleSkeleton();
      var cart = event && event.detail && event.detail.cart;
      if (cart) this._renderForCart(cart);
      else this._loadFromCartApi();
    }

    _loadFromCartApi() {
      var self = this;
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { self._renderForCart(cart); })
        .catch(function () { self._setEmpty(); });
    }

    _renderForCart(cart) {
      if (this._loading) return;
      var ltlProducts = new Set(window.HC_LTL_PRODUCT_IDS || []);
      var inCart = new Set((cart.items || []).map(function (i) { return i.product_id; }));

      // PDP/explicit-context mode: always use the fixed product ID, just refresh
      // the in-cart filter when the cart changes.
      if (this._fixedContextId) {
        this._fetchRecommendations(this._fixedContextId, ltlProducts, inCart);
        return;
      }

      var eligible = (cart.items || []).filter(function (i) {
        return !ltlProducts.has(i.product_id) && i.requires_shipping;
      });
      eligible.sort(function (a, b) { return b.final_line_price - a.final_line_price; });

      var contextItem = eligible[0] || (cart.items || []).slice().sort(function (a, b) {
        return b.final_line_price - a.final_line_price;
      })[0];

      if (!contextItem) {
        this._setEmpty();
        return;
      }

      this._fetchRecommendations(contextItem.product_id, ltlProducts, inCart);
    }

    _fetchRecommendations(productId, ltlProducts, inCart) {
      var self = this;
      self._loading = true;

      function fetchIntent(intent) {
        var url = '/recommendations/products.json?product_id=' + encodeURIComponent(productId) +
          '&limit=' + FETCH_LIMIT + '&intent=' + intent;
        return fetch(url, { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (data) { return (data && data.products) || []; })
          .catch(function () { return []; });
      }

      fetchIntent('complementary').then(function (complements) {
        var filtered = complements.filter(function (p) {
          return p.available && !ltlProducts.has(p.id) && !inCart.has(p.id);
        });

        if (filtered.length >= self._showLimit) {
          self._loading = false;
          self._renderCards(filtered.slice(0, self._showLimit));
          return;
        }

        fetchIntent('related').then(function (related) {
          var seen = new Set(filtered.map(function (p) { return p.id; }));
          var extra = related.filter(function (p) {
            return p.available && !ltlProducts.has(p.id) && !inCart.has(p.id) && !seen.has(p.id);
          });
          var combined = filtered.concat(extra).slice(0, self._showLimit);
          self._loading = false;
          if (combined.length === 0) self._setEmpty();
          else self._renderCards(combined);
        });
      });
    }

    _setEmpty() {
      this._clearSkeletonTimer();
      this.innerHTML = '';
      this.removeAttribute('data-has-products');
      this.removeAttribute('data-state');
      this.setAttribute('aria-busy', 'false');
    }

    _renderCards(products) {
      this._clearSkeletonTimer();
      var heading = this.getAttribute('heading') || 'Pairs well with what is in your cart';
      var cards = products.map(this._cardHtml.bind(this)).join('');
      var listClass;
      if (this._variant === 'page') listClass = 'hc-upsells__grid';
      else if (this._variant === 'pdp') listClass = 'hc-upsells__grid';
      else listClass = 'hc-upsells__stack';

      this.innerHTML =
        '<div class="hc-upsells__inner">' +
          '<p class="hc-upsells__title">' + escapeHtml(heading) + '</p>' +
          '<div class="' + listClass + '" role="list">' + cards + '</div>' +
        '</div>';
      this.setAttribute('data-has-products', 'true');
      this.removeAttribute('data-state');
      this.setAttribute('aria-busy', 'false');

      var self = this;
      this.querySelectorAll('[data-add-variant]').forEach(function (btn) {
        btn.addEventListener('click', function (e) { self._addToCart(e); });
      });
    }

    _cardHtml(product) {
      var variant = (product.variants && product.variants[0]) || {};
      var imgSrc = imgUrlForProduct(product);
      var titleAttr = escapeHtml(product.title);

      return (
        '<div class="hc-upsell-card" role="listitem">' +
          (imgSrc
            ? '<a class="hc-upsell-card__img-link" href="' + escapeHtml(product.url) + '" aria-label="' + titleAttr + '">' +
                '<img class="hc-upsell-card__img" src="' + escapeHtml(imgSrc) + '" alt="" loading="lazy" width="120" height="120">' +
              '</a>'
            : '') +
          '<div class="hc-upsell-card__info">' +
            '<a class="hc-upsell-card__title" href="' + escapeHtml(product.url) + '">' + titleAttr + '</a>' +
            '<span class="hc-upsell-card__price">' + formatMoney(product.price) + '</span>' +
          '</div>' +
          '<button class="hc-upsell-card__add" type="button" data-add-variant="' + escapeHtml(variant.id) + '">Add</button>' +
        '</div>'
      );
    }

    _addToCart(e) {
      var btn = e.currentTarget;
      var variantId = btn.getAttribute('data-add-variant');
      if (!variantId) return;
      btn.disabled = true;
      var originalText = btn.textContent;
      btn.textContent = 'Adding...';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      })
        .then(function (r) { return r.json(); })
        .then(function () { return fetch('/cart.js', { headers: { Accept: 'application/json' } }); })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: cart } }));
          btn.textContent = 'Added';
          setTimeout(function () { btn.disabled = false; btn.textContent = originalText; }, 1200);
        })
        .catch(function () {
          btn.disabled = false;
          btn.textContent = originalText;
        });
    }
  }

  if (!customElements.get('harvest-cart-upsells')) {
    customElements.define('harvest-cart-upsells', HarvestCartUpsells);
  }
})();
